import {
  CROP_PRODUCT_TYPE,
  CROP_PRODUCT_TYPES,
  CROP_VIRTUAL_PRODUCT_TYPE,
  MAX_CROPS_DIGITS_AFTER_DECIMAL,
} from 'appConstants';
import type { IOrderProduct } from 'contexts/CropOrders';
import type { IEnvCropsProduct } from 'contexts/EnvCropsProducts';
import type { ICropFormikProduct } from 'types/product';

type ICropProductWithOptionalData = Omit<Partial<IOrderProduct>, 'quantity'> & {
  packageUomQtyPerPackage?: number;
  productData?: {
    packageDescription?: string;
    packageUomQtyPerPackage?: number;
    packagedItemDescription?: string;
    packProducts?: Array<{
      packageDescription?: string;
      packageUomQtyPerPackage?: number;
      ratio?: number;
    }>;
  };
  packProducts?: Array<{
    packageDescription?: string;
    packageUomQtyPerPackage?: number;
    ratio?: number;
  }>;
  quantity?: number | string;
};

type IPackProductWithQty = {
  packageUomQtyPerPackage?: number;
  ratio?: number;
};

const INVALID_NUMBER = Number.NaN;

const getNumericValue = (value?: string): number => {
  if (!value) return INVALID_NUMBER;

  const matched = value.match(/\d+(?:\.\d+)?/);
  const unitMatch = value.match(/\d+(?:\.\d+)?\s*([a-z]+)/i);

  if (!matched) return INVALID_NUMBER;

  const parsed = Number(matched[0]);
  if (!Number.isFinite(parsed) || parsed <= 0) return INVALID_NUMBER;

  const unit = unitMatch?.[1]?.toLowerCase();

  if (unit === 'ml') return parsed / 1000;

  if (unit === 'g' || unit === 'gm' || unit === 'grm' || unit === 'gms') {
    return parsed / 1000;
  }

  return parsed;
};

const getVirtualPackSize = (
  packProducts: Array<{
    packageDescription?: string;
    packageUomQtyPerPackage?: number;
    ratio?: number;
  }> = [],
) => {
  const size = packProducts.reduce((acc, packProduct) => {
    const explicitPackSize = Number(packProduct.packageUomQtyPerPackage);
    const itemSize =
      Number.isFinite(explicitPackSize) && explicitPackSize > 0
        ? explicitPackSize
        : getNumericValue(packProduct.packageDescription);

    if (!Number.isFinite(itemSize)) return acc;

    return acc + itemSize * (packProduct.ratio || 1);
  }, 0);

  return size > 0 ? size : INVALID_NUMBER;
};

export const calculateVirtualPackUomQtyPerPackage = (
  packProducts: IPackProductWithQty[] = [],
): number | undefined => {
  const total = packProducts.reduce((acc, packProduct) => {
    const packageQty = Number(packProduct.packageUomQtyPerPackage);
    const ratio = Number(packProduct.ratio);

    if (!Number.isFinite(packageQty) || packageQty <= 0) {
      return acc;
    }

    return acc + packageQty * (Number.isFinite(ratio) ? ratio : 1);
  }, 0);

  return total > 0 ? Number(total.toFixed(MAX_CROPS_DIGITS_AFTER_DECIMAL)) : undefined;
};

export const getProductTitle = (product: ICropFormikProduct) => {
  return `${product?.productData?.packagedItemDescription || ''} ${
    product.productType === CROP_PRODUCT_TYPE ? `(${product.productData.packageDescription})` : ''
  }`;
};

export const getCropProductTypeLabel = (productType: string) => {
  return productType === CROP_VIRTUAL_PRODUCT_TYPE
    ? CROP_PRODUCT_TYPES.VIRTUAL_PACK.label
    : CROP_PRODUCT_TYPES.PRODUCT.label;
};

export const getCropProductDescription = (product: IOrderProduct) => {
  return product.productType === CROP_VIRTUAL_PRODUCT_TYPE
    ? product.packagedItemDescription
    : `${product.packagedItemDescription} (${product.packageDescription})`;
};

export const getCropPackSize = (product: ICropProductWithOptionalData): number | null => {
  if (!product) return null;

  const topLevelPackSize = Number(product.packageUomQtyPerPackage);
  if (Number.isFinite(topLevelPackSize) && topLevelPackSize > 0) {
    return topLevelPackSize;
  }

  const productDataPackSize = Number(product.productData?.packageUomQtyPerPackage);
  if (Number.isFinite(productDataPackSize) && productDataPackSize > 0) {
    return productDataPackSize;
  }

  const possibleDescriptions = [
    product.productData?.packageDescription,
    product.packageDescription,
    product.productData?.packagedItemDescription,
    product.packagedItemDescription,
  ];

  const parsedDescriptionSize = possibleDescriptions
    .map((description) => getNumericValue(description))
    .find((size) => Number.isFinite(size));

  if (Number.isFinite(parsedDescriptionSize)) {
    return parsedDescriptionSize as number;
  }

  const virtualPackSize = getVirtualPackSize(
    product.productData?.packProducts || product.packProducts || [],
  );

  return Number.isFinite(virtualPackSize) ? virtualPackSize : null;
};

export const calculateCropPacksFromQuantity = (
  quantity: number | string,
  product: ICropProductWithOptionalData,
): number | null => {
  const parsedQuantity = Number(quantity);
  const packSize = getCropPackSize(product);

  if (!packSize || !Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
    return null;
  }

  return Math.ceil(parsedQuantity / packSize);
};

export const calculateCropQuantityFromPacks = (
  packs: number | string,
  product: ICropProductWithOptionalData,
): number | null => {
  const parsedPacks = Number(packs);
  const packSize = getCropPackSize(product);

  if (!packSize || !Number.isFinite(parsedPacks) || parsedPacks <= 0) {
    return null;
  }

  const quantity = parsedPacks * packSize;
  return Number.isInteger(quantity)
    ? quantity
    : Number(quantity.toFixed(MAX_CROPS_DIGITS_AFTER_DECIMAL));
};

export const getCropProductQuantity = (product: ICropProductWithOptionalData): number | null => {
  if (!product) return null;

  if (product.quantity !== undefined && product.quantity !== null) {
    const parsedQuantity = Number(product.quantity);

    if (Number.isFinite(parsedQuantity) && parsedQuantity > 0) {
      return parsedQuantity;
    }
  }

  return calculateCropQuantityFromPacks(product.packagedItemQty || 0, product);
};

export const getEnvCropsProductLabel = ({ itemDescription, itemReference }: IEnvCropsProduct) => {
  return `${itemDescription + (itemReference || '')}`;
};
